import React, { useEffect, useState, useContext, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import Sidebar from '../components/Sidebar';
import './QuotationStyles.css';

const API = 'https://cctvapi.shopkwality.in/api';

const BrowseProducts = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const limit = 20;

  // Get initial items from navigation state - ensure gst_percent is set
  useEffect(() => {
    if (location.state?.items) {
      setSelectedItems(location.state.items.map(i => ({
        ...i,
        gst_percent: i.gst_percent === undefined || i.gst_percent === null ? 18 : i.gst_percent
      })));
    }
  }, [location.state]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const categoryData = res.data?.data || res.data;
      setCategories(Array.isArray(categoryData) ? categoryData : []);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  }, [token]);

  const fetchProducts = useCallback(async (reset = false) => {
    if (loading) return;

    try {
      setLoading(true);
      const currentPage = reset ? 1 : page;
      
      const params = new URLSearchParams();
      params.append('page', String(currentPage));
      params.append('limit', String(limit));
      
      let url = '';
      
      if (search.trim()) {
        params.append('q', search.trim());
        if (selectedCategory) params.append('categoryId', selectedCategory);
        url = `${API}/products/search?${params.toString()}`;
      } else {
        if (selectedCategory) params.append('categoryId', selectedCategory);
        url = `${API}/products?${params.toString()}`;
      }
      
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data?.success) {
        const newProducts = res.data.data || [];
        
        if (reset) {
          setProducts(newProducts);
        } else {
          setProducts(prev => {
            const map = new Map();
            [...prev, ...newProducts].forEach(p => map.set(p.id, p));
            return Array.from(map.values());
          });
        }
        
        if (res.data.meta) {
          setHasMore(currentPage < res.data.meta.totalPages);
        } else {
          setHasMore(newProducts.length === limit);
        }
      }
    } catch (err) {
      console.error('Failed to load products:', err);
    } finally {
      setLoading(false);
    }
  }, [loading, page, search, selectedCategory, token]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      fetchProducts(true);
    }, 500);
    return () => clearTimeout(timer);
  }, [search, selectedCategory]);

  useEffect(() => {
    if (page > 1) {
      fetchProducts(false);
    }
  }, [page]);

  const loadMore = () => {
    if (!loading && hasMore) {
      setPage(prev => prev + 1);
    }
  };

  const getItem = (productId) => selectedItems.find(i => i.product_id === productId);

  const addOrUpdateItem = (product, delta = 1) => {
    setSelectedItems(prev => {
      const existing = prev.find(i => i.product_id === product.id);
      
      if (existing) {
        const updated = prev.map(i =>
          i.product_id === product.id
            ? { ...i, quantity: Math.max(0, i.quantity + delta) }
            : i
        );
        return updated.filter(i => i.quantity > 0);
      }
      
      return [
        ...prev,
        {
          product_id: product.id,
          name: product.name,
          image: product.images?.[0]?.image || null,
          quantity: 1,
          unit_price: product.price || 0,
          gst_percent: 18  // Always default to 18
        }
      ];
    });
  };

  const updatePrice = (productId, price) => {
    setSelectedItems(prev =>
      prev.map(i =>
        i.product_id === productId
          ? { ...i, unit_price: Math.max(0, Number(price) || 0) }
          : i
      )
    );
  };

  const done = () => {
    const from = location.state?.from;
    
    if (from === 'quotation') {
      navigate('/quotations/new', {
        state: {
          customerName: location.state?.customerName || '',
          customerMobile: location.state?.customerMobile || '',
          charges: location.state?.charges || '0',
          discount: location.state?.discount || '0',
          items: selectedItems
        }
      });
    } else if (from === 'edit-quotation') {
      navigate(`/quotations/${location.state.quotationId}/edit`, {
        state: {
          customerName: location.state?.customerName || '',
          customerMobile: location.state?.customerMobile || '',
          charges: location.state?.charges || '0',
          discount: location.state?.discount || '0',
          items: selectedItems
        }
      });
    } else {
      navigate('/quotations/new', {
        state: { items: selectedItems }
      });
    }
  };

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="dashboard-main">
        <div className="page-header-quotation">
          <div className="container">
            <h1 className="page-title">Browse Products</h1>
            <p className="page-subtitle">Select products for quotation</p>
          </div>
        </div>

        <div className="container">
          {/* Search and Filters */}
          <div className="browse-filters">
            <input
              type="text"
              placeholder="🔍 Search by name, code, or keywords..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="browse-search-input"
            />
            
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="browse-category-select"
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>

            {(search || selectedCategory) && (
              <button
                onClick={() => {
                  setSearch('');
                  setSelectedCategory('');
                }}
                className="btn btn-secondary btn-sm"
              >
                Clear Filters
              </button>
            )}
          </div>

          {/* Selected Items Counter */}
          {selectedItems.length > 0 && (
            <div className="selected-items-banner">
              <span>{selectedItems.length} item(s) selected</span>
              <button onClick={done} className="btn btn-primary btn-sm">
                Done - Add to Quotation
              </button>
            </div>
          )}

          {/* Products Grid */}
          {loading && products.length === 0 ? (
            <div className="loading-container">
              <div className="spinner large"></div>
              <p>Loading products...</p>
            </div>
          ) : products.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📦</div>
              <div className="empty-text">No products found</div>
              <div className="empty-subtext">Try adjusting your search or filters</div>
            </div>
          ) : (
            <>
              <div className="browse-products-grid">
                {products.map(product => {
                  const item = getItem(product.id);
                  const isSelected = !!item;
                  
                  return (
                    <div key={product.id} className={`browse-product-card ${isSelected ? 'selected' : ''}`}>
                      {product.images?.[0]?.image && (
                        <img 
                          src={`${API.replace("/api", "")}/uploads/products/${product.images[0].image}`}
                          alt={product.name}
                          className="product-image"
                        />
                      )}
                      
                      <div className="product-info">
                        <h3 className="product-name">{product.name}</h3>
                        {product.product_code && (
                          <p className="product-code">Code: {product.product_code}</p>
                        )}
                        <p className="product-price">₹{product.price}</p>
                        
                        {product.category && (
                          <span className="product-category">{product.category.name}</span>
                        )}
                        
                        {!isSelected ? (
                          <button
                            onClick={() => addOrUpdateItem(product, 1)}
                            className="btn btn-primary btn-sm btn-add"
                          >
                            Add to Quotation
                          </button>
                        ) : (
                          <div className="product-controls">
                            <div className="quantity-controls">
                              <button
                                onClick={() => addOrUpdateItem(product, -1)}
                                className="qty-btn"
                              >
                                -
                              </button>
                              <span className="qty-display">{item.quantity}</span>
                              <button
                                onClick={() => addOrUpdateItem(product, 1)}
                                className="qty-btn"
                              >
                                +
                              </button>
                            </div>
                            
                            <div className="price-control">
                              <label>Price:</label>
                              <input
                                type="number"
                                value={item.unit_price}
                                onChange={(e) => updatePrice(product.id, e.target.value)}
                                className="price-input-small"
                                step="0.01"
                                min="0"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Load More */}
              {hasMore && (
                <div className="load-more-container">
                  <button
                    onClick={loadMore}
                    disabled={loading}
                    className="btn btn-secondary"
                  >
                    {loading ? (
                      <>
                        <span className="spinner"></span>
                        Loading...
                      </>
                    ) : (
                      'Load More'
                    )}
                  </button>
                </div>
              )}
            </>
          )}

          {/* Footer Done Button */}
          {selectedItems.length > 0 && (
            <div className="browse-footer">
              <button onClick={done} className="btn btn-primary btn-lg">
                Done - Add {selectedItems.length} Item(s) to Quotation
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BrowseProducts;